import React, { useCallback, useState } from 'react';
import { gql, useLazyQuery, useQuery, useParams } from '@common/hooks';
import { Col, Row } from 'antd';
import { setBreadcrumbs } from 'works/legacy/lib/client-store';
import { StaffingRequestForm } from './staffing-request-form';
import { StaffingRequestTemplateDetails } from '@jobs/components';
import {
  AppContent,
  ErrorHandler,
  LoadingState,
  Title,
} from '@common/components';
import { EffectiveBillingCardQuery } from 'works/graphql/effective-billing-card';
import { getBillingCardRateByShiftType } from '@institutions/services';
import { ShiftType } from '@common/graphql';

const GET_DEPARTMENT_ID_FOR_TEMPLATE = gql`
  query currentDepartmentIdForStaffingRequestForm($id: ID!) {
    staffingRequestTemplate(id: $id) {
      id
      roleLevel
      name
      externalId
      description
      billingRegularRate
      staffingRequestType
      yearsOfExperience
      shiftsPerWeek
      hoursPerShift
      role {
        id
        abbreviation
      }
      organization {
        id
        internationalAsDefault
      }
      organizationUnit {
        id
        name
        costCenter
        department {
          id
          organizationBusiness {
            id
            name
          }
          activeNonInternalWorksMemberships {
            id
            worksUser {
              id
              name
            }
          }
        }
      }
      staffingRequestTemplateCertifications {
        id
        certification {
          id
          credential
        }
        necessityLevel
      }
      staffingRequestTemplateTechnologies {
        id
        technology {
          id
          name
        }
      }
      staffingRequestTemplateSpecialties {
        id
        clinicalSpecialty {
          id
          displayName
        }
        necessityLevel
      }
    }
  }
`;

type EstimatedCostParams = {
  shiftsPerWeek: number;
  hoursPerShift: number;
  lengthInWeeks: number;
  openPositionQuantity: number;
  shiftType?: ShiftType;
};

export function CreateStaffingRequestFormPage() {
  const { templateId }: { templateId: string | undefined } = useParams();
  const [billingCard, setBillingCard] = useState(undefined);
  const [estimatedCostParams, setEstimatedCostParams] =
    useState<EstimatedCostParams>({
      shiftsPerWeek: 0,
      hoursPerShift: 0,
      lengthInWeeks: 0,
      openPositionQuantity: 0,
      shiftType: undefined,
    });
  const [estimatedCost, setEstimatedCost] = useState({
    lowest: 0,
    highest: 0,
  });

  const { loading, error, data } = useQuery(GET_DEPARTMENT_ID_FOR_TEMPLATE, {
    variables: { id: templateId },
  });

  const [loadBillingCard, { loading: loadingBillingCard }] = useLazyQuery(
    EffectiveBillingCardQuery,
    {
      onCompleted(data) {
        setBillingCard(data?.effectiveBillingCard);
      },
    }
  );

  const billingCardRateByShiftType = getBillingCardRateByShiftType(
    billingCard,
    estimatedCostParams.shiftType
  );

  const calcEstimateCosts = useCallback(() => {
    const {
      shiftsPerWeek,
      hoursPerShift,
      lengthInWeeks,
      openPositionQuantity,
    } = estimatedCostParams;

    const baseCostMultiplier =
      shiftsPerWeek * hoursPerShift * lengthInWeeks * openPositionQuantity;

    if (baseCostMultiplier === 0) {
      setEstimatedCost({ lowest: 0, highest: 0 });
      return;
    }

    if (!billingCard) {
      const cost =
        baseCostMultiplier * data.staffingRequestTemplate.billingRegularRate;
      setEstimatedCost({ lowest: cost, highest: cost });
      return;
    }

    if (!billingCardRateByShiftType) {
      setEstimatedCost({ lowest: 0, highest: 0 });
      return;
    }

    const rates = Object.values(billingCardRateByShiftType);
    const lowestCost = baseCostMultiplier * Math.min(...rates);
    const highestCost = baseCostMultiplier * Math.max(...rates);

    setEstimatedCost({ lowest: lowestCost, highest: highestCost });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    estimatedCostParams,
    billingCard,
    data?.staffingRequestTemplate?.billingRegularRate,
  ]);

  if (loading) {
    return <LoadingState />;
  }

  const staffingRequestType =
    data?.staffingRequestTemplate?.staffingRequestType;

  setBreadcrumbs([
    {
      name: 'Staffing',
      path: `/works/staffing_requests/requests/${staffingRequestType}`,
    },
    {
      name: 'Request',
      path: `/works/staffing_requests/templates/${staffingRequestType}`,
    },
  ]);

  if (error) {
    return <ErrorHandler error={error} log="CreateStaffingRequestFormPage" />;
  }

  return (
    <>
      <AppContent>
        <Row className="create-staffing-request-form-page">
          <Col xl={16} lg={15} xs={24}>
            <div className="step-header">
              <div className="ant-descriptions-title">Step 2 of 2</div>
              <Title size="h1" weight="bold">
                New Staffing Request
              </Title>
            </div>
            <Row justify="center">
              <StaffingRequestForm
                staffingRequestTemplate={data && data.staffingRequestTemplate}
                organizationUnitId={
                  data && data.staffingRequestTemplate.organizationUnit.id
                }
                billingCard={billingCard}
                loadBillingCard={loadBillingCard}
                loadingBillingCard={loadingBillingCard}
                estimatedCostParams={estimatedCostParams}
                setEstimatedCostParams={setEstimatedCostParams}
                setEstimatedCost={setEstimatedCost}
                calcEstimateCosts={calcEstimateCosts}
              />
            </Row>
          </Col>
          <Col xl={8} lg={9} xs={24}>
            <div className="template-details-container">
              <StaffingRequestTemplateDetails
                staffingRequestTemplate={data && data.staffingRequestTemplate}
                billingCard={billingCard}
                billingCardRateByShiftType={billingCardRateByShiftType}
                estimatedCost={estimatedCost}
              />
            </div>
          </Col>
        </Row>
      </AppContent>
      <style jsx>{`
        .template-details-container {
          background-color: #f3f4f5;
          margin-top: -24px;
          margin-bottom: -24px;
          margin-left: 24px;
          margin-right: -24px;
          padding: 20px 24px;
          line-height: 24px;
          min-height: 100%;
          height: 100vh;
        }

        .ant-descriptions-title {
          margin-bottom: 16px;
        }

        @media screen and (max-width: 992px) {
          .template-details-container {
            margin-left: -24px;
            margin-top: 24px;
            height: 100%;
          }
        }
      `}</style>
    </>
  );
}
